import React, { useEffect, useState, useCallback } from "react";
import {
  X,
  ExternalLink,
  GitBranch,
  Clock,
  FileCode,
  Shield,
  AlertTriangle,
  CheckCircle2,
  EyeOff,
  Copy,
  ChevronRight,
  Flame,
  Zap,
  TrendingUp,
  Target,
  Sparkles,
  Code2,
} from "lucide-react";
import SeverityBadge, { Severity } from "./SeverityBadge";
import CodeSnippet from "./CodeSnippet";
import { getRemediation } from "../../api/ai";

interface FindingDetail {
  id: string;
  severity: Severity;
  title: string;
  description?: string;
  ruleId?: string;
  tool: string;
  kind: string;
  file?: string;
  line?: number;
  endLine?: number;
  snippet?: string;
  codeContext?: string;  // Extended context around the snippet
  status: "open" | "fixed" | "suppressed";
  firstSeen?: string;
  lastSeen?: string;
  cweId?: string;
  cveId?: string;
  fixGuidance?: string;
  references?: string[];
  // P0 Features
  priorityScore?: number;
  riskScore?: number;
  isKev?: boolean;
  epssScore?: number;
  cvssScore?: number;
  cvssVector?: string;
  isNew?: boolean;
  isReachable?: boolean;
  recommendation?: string;
  introducedAt?: string;
  fixedAt?: string;
  // Enhanced fields
  owasp?: string[];           // OWASP Top 10 categories (e.g., ["A03"])
  owaspNames?: string[];      // Full OWASP names (e.g., ["A03: Injection"])
  confidence?: string;        // low, medium, high
  language?: string;          // Programming language
  fixVersion?: string;        // For SCA: version with fix
  // Dataflow for CodeQL findings
  dataflow?: Array<{ file: string; line: number; message: string }>;
}

interface FindingDrawerProps {
  finding: FindingDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onSuppress?: (finding: FindingDetail) => void;
  onMarkFixed?: (finding: FindingDetail) => void;
}

export default function FindingDrawer({
  finding,
  isOpen,
  onClose,
  onSuppress,
  onMarkFixed,
}: FindingDrawerProps) {
  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      const prevOverflow = document.body.style.overflow;
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = prevOverflow;
      };
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !finding) return null;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="drawer-overlay animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div className="drawer-panel overflow-hidden flex flex-col" role="dialog" aria-modal="true" aria-labelledby="finding-drawer-title">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-divider px-6 py-4 bg-surface1">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <SeverityBadge severity={finding.severity} />
              <span className="text-xs text-muted font-mono">{finding.ruleId}</span>
            </div>
            <h2 id="finding-drawer-title" className="mt-2 text-lg font-semibold text-text leading-tight">
              {finding.title}
            </h2>
            <div className="mt-2 flex items-center gap-3 text-xs text-muted">
              <span className="flex items-center gap-1">
                <Shield className="h-3.5 w-3.5" />
                Paxley Engine
              </span>
              <span>•</span>
              <span className="uppercase">{finding.kind}</span>
              {finding.cweId && (
                <>
                  <span>•</span>
                  <a
                    href={`https://cwe.mitre.org/data/definitions/${finding.cweId.replace("CWE-", "")}.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand hover:underline"
                  >
                    {finding.cweId}
                  </a>
                </>
              )}
            </div>
          </div>
          <button onClick={onClose} className="btn-icon flex-shrink-0" aria-label="Close finding details">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Status + Risk Badges */}
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ring-1 ${
                finding.status === "open"
                  ? "bg-critical/12 text-critical ring-critical/25"
                  : finding.status === "fixed"
                  ? "bg-success/12 text-success ring-success/25"
                  : "bg-muted/12 text-muted ring-muted/25"
              }`}
            >
              {finding.status === "open" && <AlertTriangle className="h-4 w-4" />}
              {finding.status === "fixed" && <CheckCircle2 className="h-4 w-4" />}
              {finding.status === "suppressed" && <EyeOff className="h-4 w-4" />}
              {finding.status.charAt(0).toUpperCase() + finding.status.slice(1)}
            </span>
            
            {/* KEV Badge */}
            {finding.isKev && (
              <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold bg-critical/20 text-critical ring-1 ring-critical/30">
                <Flame className="h-4 w-4" />
                Known Exploited
              </span>
            )}
            
            {/* New Finding Badge */}
            {finding.isNew && (
              <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium bg-brand/15 text-brand ring-1 ring-brand/30">
                <Sparkles className="h-4 w-4" />
                New
              </span>
            )}
            
            {/* OWASP Top 10 Badges */}
            {finding.owaspNames && finding.owaspNames.length > 0 && (
              finding.owaspNames.slice(0, 2).map((owasp, idx) => (
                <a
                  key={idx}
                  href="https://owasp.org/Top10/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium bg-warning/15 text-warning ring-1 ring-warning/30 hover:bg-warning/25 transition-colors"
                  title={`OWASP Top 10 2021: ${owasp}`}
                >
                  <Shield className="h-3.5 w-3.5" />
                  {owasp.split(":")[0]}
                </a>
              ))
            )}
            
            {/* Confidence Badge */}
            {finding.confidence && (
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ring-1 ${
                finding.confidence === 'high' 
                  ? 'bg-success/15 text-success ring-success/30'
                  : finding.confidence === 'medium'
                  ? 'bg-warning/15 text-warning ring-warning/30'
                  : 'bg-muted/15 text-muted ring-muted/30'
              }`}>
                <Target className="h-3.5 w-3.5" />
                {finding.confidence.charAt(0).toUpperCase() + finding.confidence.slice(1)} Confidence
              </span>
            )}
          </div>

          {/* Risk Insights Panel */}
          {(finding.priorityScore || finding.riskScore || finding.epssScore || finding.cvssScore) && (
            <div className="card p-4 bg-surface2/50">
              <h3 className="text-sm font-medium text-text2 mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Risk Insights
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {finding.priorityScore !== undefined && (
                  <div>
                    <div className="text-xs text-muted mb-1">Priority Score</div>
                    <div className="flex items-center gap-2">
                      <Zap className={`h-4 w-4 ${finding.priorityScore >= 70 ? 'text-critical' : finding.priorityScore >= 40 ? 'text-medium' : 'text-low'}`} />
                      <span className="text-lg font-bold text-text">{finding.priorityScore}</span>
                      <span className="text-xs text-muted">/ 100</span>
                    </div>
                  </div>
                )}
                {finding.riskScore !== undefined && (
                  <div>
                    <div className="text-xs text-muted mb-1">Risk Score</div>
                    <div className="flex items-center gap-2">
                      <Target className={`h-4 w-4 ${finding.riskScore >= 70 ? 'text-critical' : finding.riskScore >= 40 ? 'text-medium' : 'text-low'}`} />
                      <span className="text-lg font-bold text-text">{finding.riskScore}</span>
                      <span className="text-xs text-muted">/ 100</span>
                    </div>
                  </div>
                )}
                {finding.epssScore !== undefined && finding.epssScore > 0 && (
                  <div>
                    <div className="text-xs text-muted mb-1">EPSS (Exploit Probability)</div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-text">{(finding.epssScore * 100).toFixed(2)}%</span>
                    </div>
                  </div>
                )}
                {finding.cvssScore !== undefined && finding.cvssScore > 0 && (
                  <div>
                    <div className="text-xs text-muted mb-1">CVSS Base Score</div>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold ${finding.cvssScore >= 9 ? 'text-critical' : finding.cvssScore >= 7 ? 'text-high' : finding.cvssScore >= 4 ? 'text-medium' : 'text-low'}`}>
                        {finding.cvssScore.toFixed(1)}
                      </span>
                      <span className="text-xs text-muted">/ 10</span>
                    </div>
                  </div>
                )}
                {finding.fixVersion && (
                  <div>
                    <div className="text-xs text-muted mb-1">Fix Available</div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-success" />
                      <span className="text-sm font-medium text-success">
                        Upgrade to v{finding.fixVersion}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* CVSS Vector */}
              {finding.cvssVector && (
                <div className="mt-3 pt-3 border-t border-divider">
                  <div className="text-xs text-muted mb-1">CVSS Vector</div>
                  <code className="text-xs font-mono text-text2 bg-surface2 px-2 py-1 rounded break-all">
                    {finding.cvssVector}
                  </code>
                </div>
              )}
              
              {/* CVE Link */}
              {finding.cveId && (
                <div className="mt-3 pt-3 border-t border-divider">
                  <a
                    href={`https://nvd.nist.gov/vuln/detail/${finding.cveId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-critical hover:underline"
                  >
                    <Shield className="h-4 w-4" />
                    {finding.cveId}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {finding.description && (
            <div>
              <h3 className="text-sm font-medium text-text2 mb-2">Description</h3>
              <p className="text-sm text-muted leading-relaxed">{finding.description}</p>
            </div>
          )}

          {/* Vulnerable Code - Prominently displayed */}
          {(finding.snippet || finding.codeContext) && (
            <div>
              <h3 className="text-sm font-medium text-text2 mb-2 flex items-center gap-2">
                <Code2 className="h-4 w-4 text-critical" />
                Vulnerable Code
              </h3>
              <CodeSnippet
                code={finding.codeContext || finding.snippet || ""}
                filename={finding.file}
                startLine={finding.line ? Math.max(1, finding.line - 3) : 1}
                highlightLines={finding.line ? [finding.line] : []}
                maxHeight="300px"
                title={finding.file ? `${finding.file}:${finding.line || ""}` : undefined}
              />
            </div>
          )}

          {/* Location (when no snippet available) */}
          {finding.file && !finding.snippet && !finding.codeContext && (
            <div>
              <h3 className="text-sm font-medium text-text2 mb-2">Location</h3>
              <div className="card p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-mono">
                    <FileCode className="h-4 w-4 text-muted" />
                    <span className="text-text">{finding.file}</span>
                    {finding.line && (
                      <span className="text-brand">
                        :{finding.line}
                        {finding.endLine && finding.endLine !== finding.line && `-${finding.endLine}`}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => copyToClipboard(`${finding.file}:${finding.line}`)}
                    className="btn-icon h-7 w-7"
                    title="Copy path"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="mt-3 p-3 bg-surface2/50 rounded-lg text-xs text-muted flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Code snippet not available. Run a new scan to capture code context.</span>
                </div>
              </div>
            </div>
          )}

          {/* Dataflow trace for deep analysis findings */}
          {finding.dataflow && finding.dataflow.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-text2 mb-2 flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-brand" />
                Data Flow Trace
              </h3>
              <div className="card p-3 space-y-2">
                {finding.dataflow.map((step, i) => (
                  <div 
                    key={i} 
                    className="flex items-start gap-3 text-sm"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand/20 text-brand flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-text">
                        {step.file}:{step.line}
                      </div>
                      {step.message && (
                        <div className="text-xs text-muted mt-0.5">{step.message}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fix guidance */}
          {finding.fixGuidance && (
            <div>
              <h3 className="text-sm font-medium text-text2 mb-2">How to Fix</h3>
              <div className="card p-4 bg-success/5 border-success/20">
                <p className="text-sm text-text leading-relaxed">{finding.fixGuidance}</p>
              </div>
            </div>
          )}

          {/* AI Recommendation */}
          {finding.recommendation && !finding.fixGuidance && (
            <div>
              <h3 className="text-sm font-medium text-text2 mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-brand" />
                Recommendation
              </h3>
              <div className="card p-4 bg-brand/5 border-brand/20">
                <p className="text-sm text-text leading-relaxed">{finding.recommendation}</p>
              </div>
            </div>
          )}

          {/* AI Suggested Fix */}
          <AIRemediationSection findingId={finding.id} />

          {/* References */}
          {finding.references && finding.references.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-text2 mb-2">References</h3>
              <ul className="space-y-1">
                {finding.references.map((ref, i) => {
                  let displayText = ref;
                  try { displayText = new URL(ref).hostname; } catch { /* use raw ref */ }
                  return (
                  <li key={i}>
                    <a
                      href={ref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-brand hover:underline"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {displayText}
                    </a>
                  </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Timeline */}
          <div>
            <h3 className="text-sm font-medium text-text2 mb-2">Timeline</h3>
            <div className="space-y-2 text-sm">
              {finding.firstSeen && (
                <div className="flex items-center gap-2 text-muted">
                  <Clock className="h-4 w-4" />
                  First seen: {new Date(finding.firstSeen).toLocaleDateString()}
                </div>
              )}
              {finding.lastSeen && (
                <div className="flex items-center gap-2 text-muted">
                  <Clock className="h-4 w-4" />
                  Last seen: {new Date(finding.lastSeen).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-t border-divider px-6 py-4 bg-surface1 flex items-center justify-between gap-3">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
          <div className="flex items-center gap-2">
            {finding.status === "open" && onSuppress && (
              <button
                onClick={() => onSuppress(finding)}
                className="btn-ghost"
              >
                <EyeOff className="h-4 w-4" />
                Suppress
              </button>
            )}
            {finding.status === "open" && onMarkFixed && (
              <button
                onClick={() => onMarkFixed(finding)}
                className="btn-primary"
              >
                <CheckCircle2 className="h-4 w-4" />
                Mark Fixed
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── AI Remediation Sub-component ────────────────────────────────────

interface AIFix {
  is_false_positive?: boolean;
  fp_confidence?: number;
  fp_reasoning?: string;
  vulnerability?: string;
  severity_justification?: string;
  fix_description?: string;
  fixed_code?: string;
  original_code?: string;
  why_it_works?: string;
  additional_recommendations?: string[];
  references?: string[];
}

function parseAIFix(raw: string): AIFix | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function AIRemediationSection({ findingId }: { findingId: string }) {
  const [rawFix, setRawFix] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const fetchRemediation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getRemediation(findingId);
      setRawFix(result.fix);
    } catch (err: any) {
      setError(err?.status === 503 ? "AI features are not configured on this instance." : "Failed to generate fix. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [findingId]);

  useEffect(() => {
    setRawFix(null);
    setError(null);
    setCopiedField(null);
  }, [findingId]);

  const copyText = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {}
  };

  const fix = rawFix ? parseAIFix(rawFix) : null;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand" />
          AI Security Analysis
        </h3>
        {!rawFix && !loading && (
          <button onClick={fetchRemediation} disabled={!!error} className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            Analyze & Fix
          </button>
        )}
        {rawFix && (
          <button onClick={fetchRemediation} className="btn-ghost text-xs py-1 px-2 flex items-center gap-1 text-muted">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15"/></svg>
            Regenerate
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="card p-8 bg-gradient-to-br from-brand/5 to-emerald-500/5 border-brand/20 text-center space-y-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 mx-auto">
            <svg className="animate-spin h-5 w-5 text-brand" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <div className="text-sm font-medium text-text">Analyzing vulnerability...</div>
          <div className="text-xs text-muted">Paxley AI is reviewing the code context and generating a production-ready fix</div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="card p-4 bg-critical/5 border-critical/20 flex items-center gap-3">
          <AlertTriangle className="h-4 w-4 text-critical flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-critical font-medium">Analysis Failed</p>
            <p className="text-xs text-muted mt-0.5">{error}</p>
          </div>
          <button onClick={() => { setError(null); fetchRemediation(); }} className="btn-secondary text-xs py-1 px-3">Retry</button>
        </div>
      )}

      {/* Structured Result */}
      {fix && (
        <div className="space-y-3">
          {/* False Positive Banner */}
          {fix.is_false_positive && (
            <div className="card p-4 border-l-4 border-amber-400/60 bg-amber-400/10">
              <div className="flex items-center gap-2 mb-2">
                <svg className="h-5 w-5 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                <div className="text-sm font-bold text-amber-600">Likely False Positive</div>
                {fix.fp_confidence != null && (
                  <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-amber-400/20 text-amber-600 px-2 py-0.5 rounded-full">{fix.fp_confidence}% confidence</span>
                )}
              </div>
              <p className="text-sm text-text leading-relaxed">{fix.fp_reasoning}</p>
              {fix.why_it_works && (
                <p className="text-xs text-muted mt-2 pt-2 border-t border-amber-400/20">{fix.why_it_works}</p>
              )}
            </div>
          )}

          {/* Vulnerability Explanation (only for real vulnerabilities) */}
          {fix.vulnerability && !fix.is_false_positive && (
            <div className="card p-4 border-l-4 border-critical/40 bg-critical/5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-critical mb-1.5">Vulnerability</div>
              <p className="text-sm text-text leading-relaxed">{fix.vulnerability}</p>
            </div>
          )}

          {/* What the scanner flagged (for FPs, show brief context) */}
          {fix.vulnerability && fix.is_false_positive && (
            <div className="card p-4 bg-surface2/30">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1.5">What The Scanner Flagged</div>
              <p className="text-sm text-text2 leading-relaxed">{fix.vulnerability}</p>
            </div>
          )}

          {/* Fix Description (only for real vulnerabilities) */}
          {fix.fix_description && !fix.is_false_positive && (
            <div className="card p-4 border-l-4 border-success/40 bg-success/5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-success mb-1.5">Recommended Fix</div>
              <p className="text-sm text-text leading-relaxed">{fix.fix_description}</p>
            </div>
          )}

          {/* Code: Before → After (only for real vulnerabilities) */}
          {(fix.original_code || fix.fixed_code) && !fix.is_false_positive && (
            <div className="card overflow-hidden">
              {/* Original Code */}
              {fix.original_code && (
                <div>
                  <div className="flex items-center justify-between px-4 py-2 bg-critical/8 border-b border-critical/10">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-critical flex items-center gap-1.5">
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Vulnerable Code (Remove)
                    </span>
                  </div>
                  <pre className="px-4 py-3 text-xs font-mono text-critical/80 bg-critical/3 overflow-x-auto whitespace-pre-wrap leading-relaxed border-b border-divider/20">{fix.original_code}</pre>
                </div>
              )}

              {/* Fixed Code */}
              {fix.fixed_code && (
                <div>
                  <div className="flex items-center justify-between px-4 py-2 bg-success/8 border-b border-success/10">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-success flex items-center gap-1.5">
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Fixed Code (Replace With)
                    </span>
                    <button onClick={() => copyText(fix.fixed_code || "", "fixed")} className="flex items-center gap-1 text-xs text-muted hover:text-text transition-colors">
                      {copiedField === "fixed" ? <><CheckCircle2 className="h-3 w-3 text-success" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                    </button>
                  </div>
                  <pre className="px-4 py-3 text-xs font-mono text-success/90 bg-success/3 overflow-x-auto whitespace-pre-wrap leading-relaxed">{fix.fixed_code}</pre>
                </div>
              )}
            </div>
          )}

          {/* Why It Works (only for real vulnerabilities) */}
          {fix.why_it_works && !fix.is_false_positive && (
            <div className="card p-4 bg-brand/5 border-brand/15">
              <div className="text-[10px] font-bold uppercase tracking-wider text-brand mb-1.5">Why This Fix Works</div>
              <p className="text-sm text-text leading-relaxed">{fix.why_it_works}</p>
            </div>
          )}

          {/* Additional Recommendations */}
          {fix.additional_recommendations && fix.additional_recommendations.length > 0 && (
            <div className="card p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted mb-2">Additional Recommendations</div>
              <ul className="space-y-1.5">
                {fix.additional_recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-text2">
                    <ChevronRight className="h-3 w-3 text-brand flex-shrink-0 mt-0.5" />
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* References */}
          {fix.references && fix.references.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {fix.references.map((ref, i) => (
                <span key={i} className="inline-block px-2 py-0.5 rounded bg-surface2 text-[10px] font-medium text-muted">{ref}</span>
              ))}
            </div>
          )}

          {/* Powered by badge */}
          <div className="flex items-center gap-1.5 pt-1">
            <Sparkles className="h-3 w-3 text-brand/40" />
            <span className="text-[10px] text-muted/50">Powered by Paxley AI — review before applying</span>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!rawFix && !loading && !error && (
        <div className="card p-5 bg-gradient-to-br from-surface2/30 to-brand/5 border-dashed border-brand/15 text-center space-y-2">
          <Sparkles className="h-5 w-5 text-brand/30 mx-auto" />
          <p className="text-xs text-muted">Get an AI-powered security analysis with a production-ready code fix</p>
        </div>
      )}
    </div>
  );
}
